import { useState, useCallback } from "react";
import { useNotify, useRefresh } from "ra-core";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import { Progress } from "@/components/ui/progress";
import { Loader2, Send, Eye, CheckCircle, XCircle, MessageSquare } from "lucide-react";

interface BulkResult {
    contact_id: number;
    contact_name: string;
    message: string;
    status: "success" | "error";
    error?: string;
}

interface BulkSMSModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    contactIds: number[];
}

type ModalState = "preview" | "generating" | "preview_results" | "sending" | "complete";

export const BulkSMSModal = ({ open, onOpenChange, contactIds }: BulkSMSModalProps) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [state, setState] = useState<ModalState>("preview");
    const [results, setResults] = useState<BulkResult[]>([]);
    const [progress, setProgress] = useState(0);

    const generateMessages = useCallback(async (sendAfter = false) => {
        setState("generating");
        setProgress(10);

        try {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

            const response = await fetch(`${supabaseUrl}/functions/v1/ai-sms-bulk`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${supabaseKey}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    contact_ids: contactIds,
                    preview_only: !sendAfter,
                }),
            });

            setProgress(80);

            if (!response.ok) {
                throw new Error("Failed to generate messages");
            }

            const data = await response.json();
            setResults(data.results || []);
            setProgress(100);

            if (sendAfter) {
                // TODO: Actually send the messages via sms-send
                setState("complete");
                notify("SMS messages generated successfully (mock mode)", { type: "success" });
            } else {
                setState("preview_results");
            }
        } catch (error) {
            notify(`Error: ${error}`, { type: "error" });
            setState("preview");
        }
    }, [contactIds, notify]);

    const handleSend = useCallback(async () => {
        setState("sending");
        setProgress(0);

        // For now, just simulate sending (mock mode)
        // A real implementation would call sms-send for each message
        for (let i = 0; i < results.length; i++) {
            await new Promise((resolve) => setTimeout(resolve, 200));
            setProgress(((i + 1) / results.length) * 100);
        }

        setState("complete");
        notify(`Successfully sent ${results.filter(r => r.status === "success").length} SMS messages (mock mode)`, { type: "success" });
        refresh();
    }, [results, notify, refresh]);

    const handleClose = () => {
        onOpenChange(false);
        // Reset state after closing
        setTimeout(() => {
            setState("preview");
            setResults([]);
            setProgress(0);
        }, 200);
    };

    const successCount = results.filter(r => r.status === "success").length;
    const errorCount = results.filter(r => r.status === "error").length;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5" />
                        Bulk SMS - {contactIds.length} Contacts
                    </DialogTitle>
                    <DialogDescription>
                        Generate AI-powered SMS messages for selected contacts
                    </DialogDescription>
                </DialogHeader>

                {state === "preview" && (
                    <div className="py-4">
                        <p className="text-sm text-muted-foreground mb-4">
                            You have selected <strong>{contactIds.length}</strong> contacts.
                            Click "Preview Messages" to see AI-generated SMS messages before sending.
                        </p>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                            <strong>Note:</strong> Messages will be personalized based on each contact's
                            recent conversation history.
                        </div>
                    </div>
                )}

                {(state === "generating" || state === "sending") && (
                    <div className="py-8 space-y-4">
                        <div className="flex items-center justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                        <p className="text-center text-sm text-muted-foreground">
                            {state === "generating" ? "Generating AI messages..." : "Sending SMS messages..."}
                        </p>
                        <Progress value={progress} className="w-full" />
                    </div>
                )}

                {state === "preview_results" && (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2">
                            <Badge variant="default" className="bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                {successCount} Ready
                            </Badge>
                            {errorCount > 0 && (
                                <Badge variant="destructive">
                                    <XCircle className="h-3 w-3 mr-1" />
                                    {errorCount} Errors
                                </Badge>
                            )}
                        </div>
                        <div className="h-64 rounded-lg border overflow-y-auto">
                            <div className="p-4 space-y-3">
                                {results.map((result) => (
                                    <div
                                        key={result.contact_id}
                                        className={`p-3 rounded-lg border ${result.status === "success"
                                            ? "bg-green-50 border-green-200"
                                            : "bg-red-50 border-red-200"
                                            }`}
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="font-medium text-sm">{result.contact_name}</span>
                                            <span className="text-xs text-muted-foreground">
                                                {result.message.length} chars
                                            </span>
                                        </div>
                                        {result.status === "success" ? (
                                            <p className="text-sm">{result.message}</p>
                                        ) : (
                                            <p className="text-sm text-red-600">{result.error}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {state === "complete" && (
                    <div className="py-8 text-center space-y-4">
                        <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
                        <div>
                            <p className="font-medium">SMS messages sent successfully!</p>
                            <p className="text-sm text-muted-foreground">
                                {successCount} messages were sent (mock mode)
                            </p>
                        </div>
                    </div>
                )}

                <DialogFooter>
                    {state === "preview" && (
                        <>
                            <Button variant="outline" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button onClick={() => generateMessages(false)}>
                                <Eye className="h-4 w-4 mr-2" />
                                Preview Messages
                            </Button>
                        </>
                    )}

                    {state === "preview_results" && (
                        <>
                            <Button variant="outline" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSend}
                                disabled={successCount === 0}
                            >
                                <Send className="h-4 w-4 mr-2" />
                                Send {successCount} Messages
                            </Button>
                        </>
                    )}

                    {state === "complete" && (
                        <Button onClick={handleClose}>
                            Done
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
